// src/modules/app-views/invoices/components/CardHeaderInvoiceTypeStatusTotal.jsx
// *******************************************************************************
// CARD HEADER: Invoice Number, Issue Date, Invoice Total
// *******************************************************************************
// Column 1 of the invoice document card header.
// Shows: Invoice Number (serie-number), Issue Date, and Invoice Total.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Form } from 'antd';
import SimpleColumn from 'components/SimpleComponents/SimpleColumn';
import { NumberOutlined, CalendarOutlined, DollarOutlined } from '@ant-design/icons';
import { parseDateToDisplay } from 'utils/helpers';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import financialService from 'services/financialService';
import appService from 'services/appService';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const CardHeaderInvoiceNumberDateTotal = ({
  showItem1 = true,
  showItem2 = true,
  showItem3 = true,
  forceShow = false,
  ...record
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch form fields to update dynamically
  const formIssueDate = Form.useWatch('issueDate', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Invoice number (number - serie format)
  const serie = record?.documentSerie || record?.serie || 1;
  const invoiceNumber = record?.documentNumber || record?.number || record?.invoiceNumber;
  const displayNumber = invoiceNumber ? `${invoiceNumber}-${serie}` : null;

  // Issue date
  // const issueDate = formIssueDate || record?.issueDate || record?.issuedAt;
  // const issuedAt = issueDate && parseDateToDisplay({ date: issueDate, showHours: false });
  const issuedAt =
    record?.issueDate && parseDateToDisplay({ date: record?.issueDate, showHours: false });

  // Invoice total (documentAmount is the main field, invoiceTotal is fallback)
  const invoiceTotal = record?.totals?.documentAmount ?? record?.totals?.invoiceTotal;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const items = [
    {
      key: 'invoiceNumber',
      icon: <NumberOutlined />,
      label: displayNumber || <span className="text-muted opacity-0-6">N/D</span>,
      tooltipText: translateX('invoice_number'),
      tooltipPlacement: 'right',
      onClick: record?.accessKey
        ? () => appService.handleCopyToClipboard(record.accessKey, 'fiscal_key_copied')
        : null,
      hidden: !showItem1,
    },
    {
      key: 'issuedAt',
      icon: <CalendarOutlined />,
      label: issuedAt || <span className="text-muted opacity-0-6">N/D</span>,
      tooltipText: translateX('issued_date'),
      tooltipPlacement: 'right',
      onClick: null,
      hidden: !showItem2,
    },
    {
      key: 'invoiceTotal',
      icon: <DollarOutlined />,
      label:
        invoiceTotal !== undefined && invoiceTotal !== null ? (
          // <span style={{ fontWeight: 600, color: '#52c41a' }}>
          <strong>{financialService.formatCurrency(invoiceTotal)}</strong>
        ) : (
          // </span>
          <span className="text-muted opacity-0-6">N/D</span>
        ),
      tooltipText: translateX('invoice_value'),
      tooltipPlacement: 'right',
      onClick: null,
      hidden: !showItem3,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return <SimpleColumn items={items?.filter(i => (i?.label || forceShow) && !i.hidden)} />;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderInvoiceNumberDateTotal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
